import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ChartBar, Buildings, Users, SignOut, Gear, FileText, ShieldCheck, Lightning, ShoppingCart, ArrowsClockwise, Pulse, Stethoscope, GridFour, Upload, ClipboardText, MagnifyingGlass, MagicWand, Pause, Coins, Sliders, Article, ListChecks, ArrowsLeftRight } from "@phosphor-icons/react";
import { Scissors } from "lucide-react";
import API from "@/lib/api";

const STATIC_NAV_ITEMS = [
  { to: "/admin", label: "Dashboard", icon: ChartBar, end: true },
  { to: "/admin/daily-ops", label: "Daily Ops", icon: ListChecks },
  { to: "/admin/brands", label: "Brands", icon: Buildings },
  { to: "/admin/brands-sync", label: "Impact Sync", icon: ArrowsClockwise },
  { to: "/admin/publishers", label: "Publishers", icon: Users },
  { to: "/admin/pause-center", label: "Pause Center", icon: Pause },
  { to: "/admin/best-payout", label: "Best Payout", icon: Coins },
  { to: "/admin/publisher-performance", label: "Publisher Performance", icon: ChartBar },
  { to: "/admin/coverage-matrix", label: "Coverage Matrix", icon: GridFour },
  { to: "/admin/deep-search", label: "Deep Search", icon: MagnifyingGlass },
  { to: "/admin/yieldkit", label: "Yieldkit", icon: Lightning },
  { to: "/admin/yieldkit/clicks", label: "YK Click Log", icon: ClipboardText },
  { to: "/admin/yieldkit/conversions", label: "YK Conversions", icon: Coins },
  { to: "/admin/siteplug", label: "Siteplug", icon: Lightning },
  { to: "/admin/giddyup", label: "GiddyUp", icon: Lightning },
  { to: "/admin/rga", label: "LD", icon: Lightning },
  { to: "/admin/shopnomix", label: "Shopnomix (Amazon)", icon: ShoppingCart },
  // Amazon variants (Amazon 2, Amazon 3 …) inject here dynamically
  // — see useAmazonVariants() below.
  { to: "/admin/admedia", label: "AdMedia", icon: Upload },
  { to: "/admin/maxbounty", label: "MaxBounty", icon: Lightning },
  { to: "/admin/fatcoupon", label: "FatCoupon", icon: Lightning },
  { to: "/admin/flexoffers-reconciliation", label: "FlexOffers Recon", icon: Coins },
  { to: "/admin/auto-article", label: "Auto-Article", icon: Article },
  { to: "/admin/reports", label: "Reports", icon: FileText },
  { to: "/admin/ledger-corrections", label: "Ledger Corrections", icon: Coins },
  { to: "/admin/shave-actions", label: "Shave Actions", icon: Scissors },
  { to: "/admin/click-cap-rules", label: "Click + Sale Caps", icon: Sliders },
  { to: "/admin/click-log", label: "Click Log", icon: ClipboardText },
  { to: "/admin/clickflare-import", label: "ClickFlare Import", icon: Upload },
  { to: "/admin/routed-brands", label: "Routed Brands", icon: ArrowsLeftRight },
  { to: "/admin/ref-policies", label: "Referrer Policy", icon: ArrowsLeftRight },
  { to: "/admin/backfill", label: "Backfill Attribution", icon: MagicWand },
  { to: "/admin/health", label: "System Health", icon: Pulse },
  { to: "/admin/diagnostics", label: "Diagnostics", icon: Stethoscope },
  { to: "/admin/audit-log", label: "Audit Log", icon: ShieldCheck },
  { to: "/admin/settings", label: "Settings", icon: Gear },
];

/** Pull the variant list once on mount; refresh on focus so newly
 *  created variants appear without a hard reload. */
function useAmazonVariants() {
  const [variants, setVariants] = useState([]);
  const didFetch = useRef(false);
  useEffect(() => {
    let cancelled = false;
    const load = () => API.get("/admin/amazon-feeds")
      .then((r) => { if (!cancelled) setVariants(r?.data?.feeds || []); })
      .catch(() => { /* sidebar must never crash — degrade gracefully */ });
    // Guard only the initial load — the focus listener must still be
    // registered on every mount, or StrictMode's first-mount cleanup would
    // leave the sidebar with no listener at all.
    if (!didFetch.current) {
      didFetch.current = true;
      load();
    }
    window.addEventListener("focus", load);
    return () => { cancelled = true; window.removeEventListener("focus", load); };
  }, []);
  return variants;
}

function buildNavItems(amazonVariants) {
  // Splice variant entries right after the Shopnomix entry so they
  // visually group with the original Amazon dashboard.
  const items = [...STATIC_NAV_ITEMS];
  const shopnomixIdx = items.findIndex((i) => i.to === "/admin/shopnomix");
  const variantItems = (amazonVariants || []).map((v) => ({
    to: `/admin/amazon-feed/${v.slug}`,
    label: v.label,
    icon: ShoppingCart,
  }));
  if (shopnomixIdx >= 0 && variantItems.length) {
    items.splice(shopnomixIdx + 1, 0, ...variantItems);
  } else if (variantItems.length) {
    items.push(...variantItems);
  }
  return items;
}

function initials(user) {
  return (user?.email || "A").charAt(0).toUpperCase();
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const amazonVariants = useAmazonVariants();
  const navItems = buildNavItems(amazonVariants);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="admin-layout">
      <aside className="w-60 border-r border-slate-200 bg-slate-50 flex flex-col shrink-0">
        {/* Brand */}
        <div className="p-5 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-slate-900 text-white flex items-center justify-center font-heading font-black text-base tracking-tight rounded-sm">
              R
            </div>
            <div>
              <h1 className="font-heading text-[15px] font-bold text-slate-900 leading-none tracking-tight">
                RedClub
              </h1>
              <p className="text-[10px] text-slate-500 mt-1 tracking-[0.2em] uppercase font-bold">
                Control Room
              </p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-0.5">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 px-3 pb-2 pt-1">
            Operations
          </p>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-[#002FA7] text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <item.icon size={18} weight={item.end ? "bold" : "regular"} />
              {item.label}
            </NavLink>
          ))}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 w-full transition-all duration-200"
            data-testid="admin-logout-btn"
          >
            <SignOut size={18} />
            Logout
          </button>
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div
              className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center font-heading font-bold text-xs tracking-tight shrink-0"
              data-testid="admin-avatar"
            >
              {initials(user)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-slate-900 truncate">
                Administrator
              </div>
              <div className="text-[10px] text-slate-500 truncate">
                {user?.email}
              </div>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
